import React, { useState, useEffect, useMemo, useCallback, useTransition } from "react";
import Card from "../../../components/Global/Card";
import { FaBoxOpen } from "react-icons/fa";
import DataTable from "../../../components/Global/DataTable";
import Modal from "../../../components/Global/Modal";
import { CustomService } from "../../../services/api/custom/CustomService";
import { connect } from "react-redux";
import GoBack from "../../../components/Utils/GoBack";
import { GiOfficeChair } from "react-icons/gi";
import { FiEdit2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// Opcional: evitar re-render da tabela em toggles do modal
const MemoDataTable = React.memo(DataTable);

const ExpedientePage = ({ isAuthenticated, user }) => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Remoção
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [toRemove, setToRemove] = useState(null);

  // Estoque
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newStock, setNewStock] = useState(0);

  // Abre modal já; preenche conteúdo com baixa prioridade
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CustomService.getRequest("/interno/expediente");
      // Se necessário filtrar por admin, ajuste aqui:
      // const filtered = !user?.admin ? res.filter(x => !x.admin) : res;
      setData(res);
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
      toast.error("Erro ao carregar produtos de expediente");
    } finally {
      setLoading(false);
    }
  }, []);

  const openStockModal = useCallback((record) => {
    setShowStockModal(true);
    startTransition(() => {
      setSelectedProduct(record);
      setNewStock(Number.isFinite(Number(record?.estoque)) ? Number(record.estoque) : 0);
    });
  }, []);

  // PATCH /interno/produto/:id/estoque  { estoque: number }
  const saveStock = useCallback(async () => {
    if (!selectedProduct?.id) return;
    const parsed = Number(newStock);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.warn("Informe um estoque válido (zero ou maior).");
      return;
    }

    // Fechar modal imediatamente
    setShowStockModal(false);

    // Update otimista
    const prev = data;
    const next = data.map((p) =>
      p.id === selectedProduct.id ? { ...p, estoque: parsed } : p
    );
    setData(next);

    try {
      await CustomService.patchRequest(
        `/interno/produto/${selectedProduct.id}/estoque`,
        { estoque: parsed }
      );
      toast.success("Estoque atualizado!");
      // Opcional: sincronizar sem travar UI
      // void fetchData();
    } catch (error) {
      console.error("Erro ao atualizar estoque:", error);
      setData(prev); // rollback
      toast.error("Não foi possível atualizar o estoque.");
    } finally {
      setSelectedProduct(null);
    }
  }, [data, newStock, selectedProduct]);

  const removeProduct = useCallback(async () => {
    try {
      await CustomService.deleteRequest(`/interno/produto/${toRemove.id}`);
      toast.success("Produto removido com sucesso!");
      setToRemove(null);
      setShowRemoveModal(false);
      setData((cur) => cur.filter((p) => p.id !== toRemove.id));
    } catch (error) {
      toast.error("Erro ao remover produto.");
      console.error("Erro ao remover produto:", error);
    }
  }, [toRemove]);

  const columns = useMemo(
    () => [
      { title: "ID", dataIndex: "fid", key: "fid" },
      { title: "Nome", dataIndex: "nome", key: "nome" },
      {
        title: "Estoque",
        dataIndex: "estoque",
        key: "estoque",
        render: (estoque, record) => (
          <div className="flex items-center gap-2">
            <span>{Number.isFinite(Number(estoque)) ? Number(estoque) : 0}</span>
            <button
              onClick={() => openStockModal(record)}
              className="text-gray-500 hover:text-indigo-600 transition"
              title="Atualizar estoque"
            >
              <FiEdit2 className="w-4 h-4" />
            </button>
          </div>
        ),
      },
      {
        title: "Preço",
        dataIndex: "preco_produto",
        key: "preco",
        render: (preco) =>
          preco
            ? (preco / 100).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })
            : "R$ 0,00",
      },
    ],
    [openStockModal]
  );

  const actions = useMemo(
    () => ({
      onDelete: (record) => {
        setToRemove(record);
        setShowRemoveModal(true);
      },
      onEdit: (record) => {
        navigate(`/editar/produto/${record.id}`);
      },
    }),
    [navigate]
  );

  return (
    <div className="flex flex-col w-full gap-3">
      <GoBack path="/dashboard" />

      {/* Modal: Remoção */}
      {showRemoveModal && (
        <Modal
          buttonName="Excluir"
          title="Confirmar exclusão"
          handleModal={setShowRemoveModal}
          eventConfirmation={removeProduct}
          size="md:w-1/3"
        >
          <div className="flex flex-col justify-center items-center gap-1 text-center">
            <span>
              Deseja excluir o produto <b>{toRemove?.nome}</b>?
            </span>
            <span className="text-[14px]">Esta ação é irreversível.</span>
          </div>
        </Modal>
      )}

      {/* Modal: Atualização de Estoque */}
      {showStockModal && (
        <Modal
          buttonName="Salvar"
          title="Atualizar estoque"
          handleModal={setShowStockModal}
          eventConfirmation={saveStock}
          size="md:w-1/3"
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <FaBoxOpen className="w-5 h-5" />
              <div className="flex flex-col">
                <span className="font-medium">
                  {selectedProduct?.nome ?? "Carregando..."}
                </span>
                <span className="text-sm text-gray-500">
                  Atual estoque:{" "}
                  <b>
                    {Number.isFinite(Number(selectedProduct?.estoque))
                      ? Number(selectedProduct?.estoque)
                      : 0}
                  </b>
                </span>
              </div>
            </div>

            <label className="flex flex-col gap-2">
              <span className="text-sm text-gray-700">Novo estoque</span>
              <input
                type="number"
                min={0}
                step={1}
                value={newStock}
                onChange={(e) => setNewStock(Number(e.target.value))}
                className="w-full rounded-md border px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="0"
              />
            </label>

            {isPending && (
              <p className="text-xs text-gray-400">carregando dados do produto…</p>
            )}
          </div>
        </Modal>
      )}

      <Card
        buttonActionPath="/cadastro/produto/expediente"
        title="Produtos de Expediente"
        icon={<GiOfficeChair className="w-[25px] h-[25px]" />}
      >
        <div className="w-full">
          <MemoDataTable
            size="100%"
            data={data}
            columns={columns}
            loading={loading}
            actions={actions}
          />
        </div>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  user: state.auth.user,
  error: state.auth.error,
});

const mapDispatchToProps = (dispatch) => ({
  login: (userData) => dispatch({ type: "LOGIN_SUCCESS", payload: userData }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpedientePage);
